/* eslint-disable react/prop-types */
import React, {useState, useRef, useCallback} from 'react';
import {
  Button,
  CircularProgress,
  TextField,
  LinearProgress,
  MenuItem,
} from '@mui/material';
import {ArrowForward, ArrowBack, CancelOutlined} from '@mui/icons-material';
import InputMask from 'react-input-mask';

const API_BASE = 'https://kops.apiireit.com/gateway';

const C = {
  azul: '#06448e',
  azulD: '#04316a',
  laranja: '#FF6600',
  laranjaD: '#cc4400',
  roxo: '#7c53e3',
  text: '#1a1a2e',
  muted: '#8a94a6',
  success: '#059669',
  danger: '#e53935',
  warn: '#d97706',
};

// ─── Data ─────────────────────────────────────────────────────────────────────
const CORES = [
  'Amarelo',
  'Azul',
  'Azul claro',
  'Bege',
  'Branco',
  'Cinza',
  'Dourado',
  'Laranja',
  'Marrom',
  'Prata',
  'Preto',
  'Rosa',
  'Roxo',
  'Verde',
  'Verde claro',
  'Vermelho',
];

const COR_SWATCHES = {
  Amarelo: '#FACC15',
  Azul: '#1D4ED8',
  'Azul claro': '#38BDF8',
  Bege: '#D2B48C',
  Branco: '#F8FAFC',
  Cinza: '#6B7280',
  Dourado: '#B45309',
  Laranja: '#EA580C',
  Marrom: '#78350F',
  Prata: '#9CA3AF',
  Preto: '#111827',
  Rosa: '#EC4899',
  Roxo: '#7C3AED',
  Verde: '#16A34A',
  'Verde claro': '#4ADE80',
  Vermelho: '#DC2626',
};

// Current year going back 16 years (matches Android spinner)
const YEARS = Array.from({length: 17}, (_, i) =>
  String(new Date().getFullYear() - i),
);

// ─── Date helpers (CRLV expiry must be future) ───────────────────────────────
function parseDMY(str) {
  if (!str || (str || '').replace(/\D/g, '').length < 8) return null;
  const p = (str || '').split('/');
  if (p.length !== 3) return null;
  const [d, m, y] = p.map(Number);
  if (!d || !m || !y) return null;
  const dt = new Date(y, m - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d
    ? dt
    : null;
}
const isFuture = str => {
  const dt = parseDMY(str);
  return dt ? dt > new Date() : null;
};
const daysUntil = str => {
  const dt = parseDMY(str);
  return dt ? Math.round((dt - new Date()) / 86400000) : null;
};

// ─── CRLV SVG guide ───────────────────────────────────────────────────────────
const CrlvOK = () => (
  <svg
    viewBox="0 0 200 130"
    width="100%"
    height="100%"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="10"
      y="10"
      width="180"
      height="110"
      rx="8"
      fill="#eef6ff"
      stroke="#06448e"
      strokeWidth="2"
    />
    <rect x="10" y="10" width="180" height="24" rx="8" fill="#06448e" />
    <text
      x="100"
      y="26"
      textAnchor="middle"
      fontSize="9"
      fill="#fff"
      fontWeight="700"
    >
      CERTIFICADO DE REGISTRO E LICENCIAMENTO
    </text>
    {/* Car icon */}
    <rect
      x="18"
      y="42"
      width="50"
      height="30"
      rx="4"
      fill="#dbeafe"
      stroke="#06448e"
      strokeWidth="1"
    />
    <rect x="24" y="35" width="38" height="12" rx="3" fill="#bfdbfe" />
    <circle cx="24" cy="73" r="5" fill="#1e3a5f" />
    <circle cx="54" cy="73" r="5" fill="#1e3a5f" />
    {/* Text lines */}
    {[0, 1, 2, 3, 4].map(i => (
      <rect
        key={i}
        x="78"
        y={42 + i * 13}
        width={[70, 55, 65, 50, 60][i]}
        height="6"
        rx="3"
        fill="#dbeafe"
      />
    ))}
    {/* Plate area */}
    <rect
      x="78"
      y="110"
      width="50"
      height="8"
      rx="2"
      fill="#06448e"
      opacity="0.15"
    />
    <text
      x="103"
      y="117"
      textAnchor="middle"
      fontSize="7"
      fill="#06448e"
      fontWeight="700"
    >
      ABC-1234
    </text>
    {/* Badge */}
    <circle cx="182" cy="18" r="12" fill="#059669" />
    <text x="182" y="22" textAnchor="middle" fontSize="12" fill="#fff">
      ✓
    </text>
    <rect
      x="10"
      y="120"
      width="180"
      height="10"
      rx="3"
      fill="#059669"
      opacity="0.1"
    />
    <text
      x="100"
      y="127"
      textAnchor="middle"
      fontSize="8"
      fill="#059669"
      fontWeight="700"
    >
      DOCUMENTO CORRETO — plano, legível, sem reflexo
    </text>
  </svg>
);

const CrlvBAD = () => (
  <svg
    viewBox="0 0 200 130"
    width="100%"
    height="100%"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="14"
      y="6"
      width="180"
      height="110"
      rx="8"
      fill="#fff5f5"
      stroke="#fca5a5"
      strokeWidth="2"
      transform="rotate(-6 100 65)"
    />
    <rect
      x="14"
      y="6"
      width="180"
      height="24"
      rx="8"
      fill="#ef4444"
      transform="rotate(-6 100 65)"
    />
    <text
      x="100"
      y="22"
      textAnchor="middle"
      fontSize="9"
      fill="#fff"
      fontWeight="700"
      transform="rotate(-6 100 65)"
    >
      CERTIFICADO DE REGISTRO
    </text>
    <ellipse cx="100" cy="60" rx="55" ry="28" fill="white" opacity="0.5" />
    <circle cx="182" cy="18" r="12" fill="#e53935" />
    <text x="182" y="22" textAnchor="middle" fontSize="12" fill="#fff">
      ✗
    </text>
    <rect
      x="10"
      y="120"
      width="180"
      height="10"
      rx="3"
      fill="#e53935"
      opacity="0.1"
    />
    <text
      x="100"
      y="127"
      textAnchor="middle"
      fontSize="8"
      fill="#e53935"
      fontWeight="700"
    >
      INCLINADO + REFLEXO — ilegível
    </text>
  </svg>
);

// ─── Upload area ──────────────────────────────────────────────────────────────
const UploadArea = ({file, existingUrl, onFile, onClear}) => {
  const ref = useRef(null);
  const [drag, setDrag] = useState(false);
  const hasDoc = file || existingUrl;
  const src = file ? URL.createObjectURL(file) : existingUrl;

  const drop = useCallback(
    e => {
      e.preventDefault();
      setDrag(false);
      const f = e.dataTransfer.files?.[0];
      if (f?.type.startsWith('image/')) onFile(f);
    },
    [onFile],
  );

  return (
    <div style={{position: 'relative'}}>
      {hasDoc && (
        <button
          onClick={onClear}
          style={{
            position: 'absolute',
            top: -10,
            right: -10,
            zIndex: 2,
            background: '#fff',
            border: 'none',
            borderRadius: '50%',
            cursor: 'pointer',
            width: 28,
            height: 28,
            boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
          }}
        >
          <CancelOutlined style={{fontSize: 18, color: C.danger}} />
        </button>
      )}
      <div
        onDragOver={e => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={drop}
        onClick={() => ref.current?.click()}
        style={{
          width: '100%',
          minHeight: hasDoc ? 180 : 120,
          borderRadius: 14,
          cursor: 'pointer',
          border: hasDoc ? 'none' : `2px dashed ${drag ? C.laranja : C.azul}`,
          background: hasDoc
            ? 'transparent'
            : drag
            ? 'rgba(255,102,0,0.05)'
            : '#f4f6fa',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          transition: 'all 0.2s',
          position: 'relative',
        }}
      >
        <input
          ref={ref}
          type="file"
          accept="image/*"
          style={{display: 'none'}}
          onChange={e => {
            const f = e.target.files?.[0];
            if (f) onFile(f);
            e.target.value = '';
          }}
        />
        {hasDoc ? (
          <>
            <img
              src={src}
              alt="preview"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: 14,
              }}
              onLoad={() => {
                if (file) URL.revokeObjectURL(src);
              }}
            />
            <div
              style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                background: 'rgba(0,0,0,0.42)',
                color: '#fff',
                padding: '7px',
                textAlign: 'center',
                fontSize: 12,
                fontWeight: 700,
                borderBottomLeftRadius: 12,
                borderBottomRightRadius: 12,
              }}
            >
              📄 Toque para trocar o documento
            </div>
          </>
        ) : (
          <div style={{textAlign: 'center', padding: 20}}>
            <div style={{fontSize: 36, marginBottom: 8}}>📄</div>
            <p
              style={{margin: 0, fontSize: 14, fontWeight: 700, color: C.azul}}
            >
              Toque para fotografar o CRLV
            </p>
            <p style={{margin: '4px 0 0', fontSize: 12, color: C.muted}}>
              JPG, PNG · máx. 10 MB
            </p>
          </div>
        )}
      </div>
      {hasDoc && (
        <div
          style={{
            marginTop: 8,
            padding: '8px 12px',
            borderRadius: 10,
            background:
              existingUrl && !file
                ? 'rgba(5,150,105,0.05)'
                : 'rgba(6,68,142,0.05)',
            border: `1px solid ${existingUrl && !file ? C.success : C.azul}20`,
            display: 'flex',
            gap: 8,
            alignItems: 'center',
          }}
        >
          <span style={{fontSize: 15}}>
            {existingUrl && !file ? '✅' : '🔍'}
          </span>
          <p
            style={{
              margin: 0,
              fontSize: 12,
              fontWeight: 600,
              color: existingUrl && !file ? C.success : C.azul,
            }}
          >
            {existingUrl && !file
              ? 'Documento salvo anteriormente. Toque para substituir se necessário.'
              : 'Confira: todos os campos legíveis, sem reflexo ou corte.'}
          </p>
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// STEP VEICULO
// ══════════════════════════════════════════════════════════════════════════════
/**
 * Props
 * ─────
 * authUser, getToken, sm
 * intencao          string — 'mototaxista'|'motorista'|'entregador' for default cat
 * vcMarca / setVcMarca
 * vcModelo / setVcModelo
 * vcAno / setVcAno
 * vcPlaca / setVcPlaca
 * vcCor / setVcCor
 * vcCat / setVcCat      'Carro'|'Moto'
 * vcExp / setVcExp      DD/MM/AAAA  (CRLV expiry)
 * vcDocFile / setVcDocFile  File|null
 * onNext () => void
 * onBack () => void
 */
const StepVeiculo = ({
  authUser,
  getToken,
  sm,
  intencao,
  existingDocUrl,
  vcMarca,
  setVcMarca,
  vcModelo,
  setVcModelo,
  vcAno,
  setVcAno,
  vcPlaca,
  setVcPlaca,
  vcCor,
  setVcCor,
  vcCat,
  setVcCat,
  vcExp,
  setVcExp,
  vcDocFile,
  setVcDocFile,
  onNext,
  onBack,
}) => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [phase, setPhase] = useState('');

  // Default category from intencao when component mounts
  React.useEffect(() => {
    if (!vcCat) {
      setVcCat(intencao === 'motorista' ? 'Carro' : 'Moto');
    }
    if (!vcAno) setVcAno(YEARS[0]);
    if (!vcCor) setVcCor('Preto');
  }, []); // eslint-disable-line

  // ── Expiry validation ──────────────────────────────────────────────────────
  const expValid =
    (vcExp || '').replace(/\D/g, '').length === 8 ? isFuture(vcExp) : null;
  const days = expValid ? daysUntil(vcExp) : null;

  // ── Validate ───────────────────────────────────────────────────────────────
  const validate = () => {
    if (!vcDocFile && !existingDocUrl)
      return 'Adicione a foto do documento do veículo (CRLV).';
    if (!vcCat) return 'Selecione a categoria (Carro ou Moto).';
    if (!(vcMarca || '').trim()) return 'Informe a marca do veículo.';
    if (!(vcModelo || '').trim()) return 'Informe o modelo do veículo.';
    if (!(vcPlaca || '').trim()) return 'Informe a placa do veículo.';
    if (!vcCor) return 'Selecione a cor do veículo.';
    if (!vcAno) return 'Selecione o ano do veículo.';
    if (expValid === false) return 'CRLV vencido ou data inválida.';
    if (expValid === null) return 'Informe a validade do CRLV (DD/MM/AAAA).';
    if (days !== null && days < 30)
      return `CRLV vence em ${days} dias. Renove antes de continuar.`;
    return null;
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError('');
    setLoading(true);

    try {
      const token = await getToken();
      const uid = authUser.uid;

      // 1. Insert vehicle record
      setPhase('saving');

      const body = {
        idmotorista: uid,
        imagem: '',
        marca: (vcMarca || '').trim(),
        veiculo: (vcModelo || '').trim(), // field name in model is "veiculo"
        placa: (vcPlaca || '').trim().toUpperCase(),
        type: vcCat, // 'Carro' or 'Moto'
        cor: vcCor,
        cyear: parseInt(vcAno, 10),
        status: 0,
        av: false,
        nota: 0,
        doctoup: '',
      };

      const saveRes = await fetch(
        `${API_BASE}/parceiros/veiculos/insert?exp=${encodeURIComponent(
          vcExp,
        )}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(body),
        },
      );

      if (!saveRes.ok) {
        throw new Error(`Erro ao cadastrar veículo (${saveRes.status}).`);
      }

      const veiculo = await saveRes.json(); // Veiculomodels — has _id
      const docId = veiculo._id;

      // 2. Upload CRLV photo — skip if reusing the existing doc
      if (vcDocFile) {
        setPhase('uploading');
        setUploadPct(10);

        const form = new FormData();
        form.append('file', vcDocFile);

        const iv = setInterval(
          () => setUploadPct(p => (p < 82 ? p + 9 : p)),
          300,
        );
        try {
          await fetch(
            `${API_BASE}/s3/upload?userid=${uid}&docid=${docId}&svc=docvei&index=0`,
            {
              method: 'POST',
              headers: {Authorization: `Bearer ${token}`},
              body: form,
            },
          );
        } catch (e) {
          console.warn('CRLV upload failed, continuing:', e);
        } finally {
          clearInterval(iv);
          setUploadPct(100);
        }
      }

      setTimeout(() => onNext(), 400);
    } catch (e) {
      setError(e.message || 'Erro ao cadastrar veículo. Tente novamente.');
      setLoading(false);
      setPhase('');
      setUploadPct(0);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  const CAT_OPTS = [
    {id: 'Moto', icon: '🏍️', label: 'Moto', desc: 'Motocicleta'},
    {id: 'Carro', icon: '🚗', label: 'Carro', desc: 'Automóvel'},
  ];

  return (
    <div>
      {/* CRLV guide */}
      <p
        style={{
          margin: '0 0 10px',
          fontSize: 15,
          fontWeight: 800,
          color: C.text,
        }}
      >
        📄 Documento do veículo (CRLV)
      </p>
      <p
        style={{
          margin: '0 0 12px',
          fontSize: 13,
          color: C.muted,
          lineHeight: 1.55,
        }}
      >
        Fotografe o CRLV (Certificado de Registro e Licenciamento de Veículo)
        plano, com todos os campos visíveis.
      </p>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 10,
          marginBottom: 14,
        }}
      >
        <div style={{height: sm ? 100 : 120}}>
          <CrlvOK />
        </div>
        <div style={{height: sm ? 100 : 120}}>
          <CrlvBAD />
        </div>
      </div>

      {/* CRLV requirements */}
      <div
        style={{
          background: '#f8f9fb',
          borderRadius: 12,
          padding: '10px 14px',
          border: '1px solid #eef0f4',
          marginBottom: 14,
        }}
      >
        {[
          {ok: true, t: 'Documento plano e completamente visível'},
          {ok: true, t: 'Foto nítida, sem cortes nas bordas'},
          {ok: true, t: 'CRLV digital (PDF/print) também é válido'},
          {ok: false, t: 'Sem reflexo de luz ou sombra sobre o texto'},
          {ok: false, t: 'Veículo não pode ser anterior a 2013'},
        ].map((r, i, arr) => (
          <div
            key={i}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '5px 0',
              borderBottom: i < arr.length - 1 ? '1px solid #f0f2f5' : 'none',
            }}
          >
            <span
              style={{
                width: 18,
                height: 18,
                borderRadius: '50%',
                background: r.ok ? `${C.success}18` : `${C.danger}18`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                flexShrink: 0,
                color: r.ok ? C.success : C.danger,
                fontWeight: 700,
              }}
            >
              {r.ok ? '✓' : '✗'}
            </span>
            <span style={{fontSize: 12, color: C.text}}>{r.t}</span>
          </div>
        ))}
      </div>

      <UploadArea
        file={vcDocFile}
        existingUrl={!vcDocFile ? existingDocUrl : null}
        onFile={setVcDocFile}
        onClear={() => setVcDocFile(null)}
      />

      <div style={{height: 1, background: '#f0f2f5', margin: '24px 0'}} />

      {/* Category */}
      <p
        style={{
          margin: '0 0 10px',
          fontSize: 15,
          fontWeight: 800,
          color: C.text,
        }}
      >
        🚘 Tipo de veículo
      </p>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 10,
          marginBottom: 20,
        }}
      >
        {CAT_OPTS.map(o => {
          const sel = vcCat === o.id;
          return (
            <button
              key={o.id}
              onClick={() => setVcCat(o.id)}
              disabled={loading}
              style={{
                padding: '14px 10px',
                borderRadius: 14,
                cursor: 'pointer',
                border: `2px solid ${sel ? C.azul : '#e0e5ef'}`,
                background: sel ? `${C.azul}0d` : '#fff',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 6,
                transition: 'all 0.15s',
                fontFamily: "'Outfit','Segoe UI',sans-serif",
              }}
            >
              <span style={{fontSize: 28}}>{o.icon}</span>
              <span
                style={{
                  fontSize: 14,
                  fontWeight: sel ? 800 : 600,
                  color: sel ? C.azul : C.text,
                }}
              >
                {o.label}
              </span>
              <span style={{fontSize: 11, color: C.muted}}>{o.desc}</span>
            </button>
          );
        })}
      </div>

      {/* Vehicle fields */}
      <p
        style={{
          margin: '0 0 14px',
          fontSize: 15,
          fontWeight: 800,
          color: C.text,
        }}
      >
        🔧 Dados do veículo
      </p>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 12,
          marginBottom: 16,
        }}
      >
        <TextField
          label="Marca *"
          variant="outlined"
          size="small"
          value={vcMarca || ''}
          onChange={e => setVcMarca(e.target.value)}
          placeholder="Honda, Yamaha…"
          disabled={loading}
        />
        <TextField
          label="Modelo *"
          variant="outlined"
          size="small"
          value={vcModelo || ''}
          onChange={e => setVcModelo(e.target.value)}
          placeholder="CG 160, Titan…"
          disabled={loading}
        />
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 12,
          marginBottom: 16,
        }}
      >
        {/* Ano */}
        <TextField
          label="Ano *"
          variant="outlined"
          size="small"
          select
          value={vcAno || YEARS[0]}
          onChange={e => setVcAno(e.target.value)}
          disabled={loading}
        >
          {YEARS.map(y => (
            <MenuItem key={y} value={y}>
              {y}
            </MenuItem>
          ))}
        </TextField>

        {/* Cor */}
        <TextField
          label="Cor *"
          variant="outlined"
          size="small"
          select
          value={vcCor || 'Preto'}
          onChange={e => setVcCor(e.target.value)}
          disabled={loading}
          SelectProps={{
            renderValue: v => (
              <span style={{display: 'flex', alignItems: 'center', gap: 8}}>
                <span
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    background: COR_SWATCHES[v] || '#ccc',
                    border: '1px solid #e0e5ef',
                    flexShrink: 0,
                  }}
                />
                {v}
              </span>
            ),
          }}
        >
          {CORES.map(c => (
            <MenuItem key={c} value={c}>
              <span style={{display: 'flex', alignItems: 'center', gap: 10}}>
                <span
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    background: COR_SWATCHES[c] || '#ccc',
                    border: '1px solid #e0e5ef',
                    flexShrink: 0,
                  }}
                />
                {c}
              </span>
            </MenuItem>
          ))}
        </TextField>
      </div>

      {/* Placa */}
      <TextField
        label="Placa *"
        variant="outlined"
        size="small"
        fullWidth
        style={{marginBottom: 16}}
        value={(vcPlaca || '').toUpperCase()}
        onChange={e => setVcPlaca(e.target.value.toUpperCase())}
        placeholder="ABC-1234 ou ABC1D23"
        inputProps={{
          maxLength: 8,
          style: {
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            fontWeight: 700,
          },
        }}
        disabled={loading}
      />

      <div style={{height: 1, background: '#f0f2f5', margin: '4px 0 20px'}} />

      {/* CRLV expiry */}
      <p
        style={{
          margin: '0 0 12px',
          fontSize: 15,
          fontWeight: 800,
          color: C.text,
        }}
      >
        📅 Validade do CRLV
      </p>

      <InputMask
        mask="99/99/9999"
        value={vcExp || ''}
        onChange={e => setVcExp(e.target.value)}
        disabled={loading}
      >
        {ip => (
          <TextField
            {...ip}
            label="Data de validade do CRLV *"
            variant="outlined"
            size="small"
            fullWidth
            placeholder="DD/MM/AAAA"
            error={expValid === false}
            helperText={
              expValid === false
                ? 'CRLV vencido ou data inválida.'
                : expValid === true && days !== null && days < 90
                ? `⚠️ Vence em ${days} dias — renove em breve.`
                : expValid === true
                ? `✓ Válido por mais ${days} dias`
                : ''
            }
            FormHelperTextProps={{
              style: {
                color:
                  expValid === true
                    ? days !== null && days < 90
                      ? C.warn
                      : C.success
                    : undefined,
                fontWeight: expValid !== null ? 600 : 400,
              },
            }}
          />
        )}
      </InputMask>

      {/* Error */}
      {error && (
        <div
          style={{
            marginTop: 14,
            padding: '10px 14px',
            borderRadius: 10,
            background: `${C.danger}0d`,
            border: `1px solid ${C.danger}30`,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span style={{fontSize: 16}}>⚠️</span>
          <p
            style={{margin: 0, fontSize: 13, color: C.danger, fontWeight: 600}}
          >
            {error}
          </p>
        </div>
      )}

      {/* Progress */}
      {loading && (
        <div style={{marginTop: 14}}>
          <p
            style={{
              margin: '0 0 6px',
              fontSize: 12,
              color: C.muted,
              fontWeight: 600,
            }}
          >
            {phase === 'saving'
              ? '⏳ Cadastrando veículo…'
              : `📤 Enviando documento… ${uploadPct}%`}
          </p>
          <LinearProgress
            variant={phase === 'uploading' ? 'determinate' : 'indeterminate'}
            value={uploadPct}
            sx={{
              borderRadius: 4,
              height: 6,
              backgroundColor: '#f0f2f5',
              '& .MuiLinearProgress-bar': {backgroundColor: C.laranja},
            }}
          />
        </div>
      )}

      {/* Nav */}
      <div
        style={{
          display: 'flex',
          gap: 12,
          marginTop: 28,
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Button
          onClick={onBack}
          startIcon={<ArrowBack />}
          disabled={loading}
          style={{textTransform: 'none', color: C.muted, fontSize: 14}}
        >
          Voltar
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading}
          endIcon={loading ? null : <ArrowForward />}
          sx={{
            bgcolor: C.laranja,
            color: '#fff',
            borderRadius: '12px',
            height: 48,
            px: 3,
            textTransform: 'none',
            fontWeight: 700,
            fontSize: 15,
            ':hover': {bgcolor: C.laranjaD},
            ':disabled': {bgcolor: '#e0e0e0', color: '#aaa'},
          }}
        >
          {loading ? (
            <CircularProgress size={20} style={{color: '#fff'}} />
          ) : (
            'Cadastrar veículo'
          )}
        </Button>
      </div>
    </div>
  );
};

export default StepVeiculo;
